import {DataFetcher, PriorityConfig} from '@aboutyou/data-fetcher';
import {TreeNavigationItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tree_navigation_item.types';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import React from 'react';
import {
  ExtendedTreeItem,
  NavigationTree,
  NavigationTreeProps,
} from '../../../features/NavigationTree/NavigationTree';
import {NavigationTreeLoading} from '../../../features/NavigationTree/NavigationTreeLoading';
import {useAppliedPotentialFilters} from '../../../features/ProductStream/utils';
import {FIRST_PRODUCT_ID_PARAM} from '../../../features/firstProductId/useFirstProductIds';
import {useCategoryLinkPaths} from '../utils/useStoryPath';
import {useStoryNavigationTree} from './hooks/useStoryNavigationTree';

export const StoryNavigationTree: React.FC<
  PriorityConfig & {
    className?: string;
  }
> = ({renderingPriority, ...otherProps}) => {
  return (
    <DataFetcher
      request={useStoryNavigationTree()}
      Error={null}
      Loading={NavigationTreeLoading}
      View={StoryNavigationTreeView}
      viewProps={{
        ...otherProps,
        trackOriginKey: 'category_tree_side' as originClick,
        testId: 'storyNavigationTree',
        // 'category' must always come from the clicked node's own targetPath
        // (absent entirely for the "All Items" root node), never survive from
        // the current URL - otherwise clicking "All Items" while on another
        // category leaves the old category param in place.
        excludeUrlParams: [
          FIRST_PRODUCT_ID_PARAM,
          'category',
        ] as NavigationTreeProps['excludeUrlParams'],
      }}
      renderingPriority={renderingPriority}
    />
  );
};

const StoryNavigationTreeView: React.FC<NavigationTreeProps> = ({
  categoryTree,
  excludeUrlParams,
  ...otherProps
}) => {
  const normalizeNodes = useNormalizeNodes();
  const appliedFilters = useAppliedPotentialFilters();
  const hasAppliedFilters = Boolean(Object.keys(appliedFilters).length);

  // The marker below lets StoryPage collapse its now-empty sidebar column
  if (categoryTree.length === 0) {
    return <span data-story-navigation-tree-empty hidden />;
  }

  return (
    <NavigationTree
      categoryTree={categoryTree.map(normalizeNodes)}
      useButtonLink={hasAppliedFilters}
      excludeUrlParams={excludeUrlParams}
      {...otherProps}
    />
  );
};

const useNormalizeNodes = () => {
  const categoryLinkPaths = useCategoryLinkPaths();

  const normalizeNode = (node: TreeNavigationItem): ExtendedTreeItem => ({
    ...node,
    category: {
      ...node.category,
      // The "All Items" root node means "no category filter" - useStoryPath
      // only produces a bare (filter-clearing) path when categoryId is falsy.
      // A greyed-out (0-product) category resets filters instead of
      // carrying them over - see useCategoryLinkPaths' resetFilters option.
      ...categoryLinkPaths(
        node.category.isRootCategory ? undefined : node.category.id,
        {resetFilters: !node.productCount},
      ),
    },
    children: node.children.map(normalizeNode),
  });

  return normalizeNode;
};
